<template>
  <div :id="id" ref="mapContainer"></div>
</template>

<script setup>
import mapboxgl from "mapbox-gl";
import { defineProps, defineEmits, onMounted, onUnmounted, ref, nextTick } from "vue";
import { accessToken, mapStyle, vikingAccessToken, vikingMapStyle } from "@/view/ancestry/recent/mapboxConfig";

const props = defineProps({
  id: { type: String, default: "map" },
  type: String,
});
const emit = defineEmits(["mapLoaded"]);

const mapContainer = ref(null);
let map = null;
let resizeObserver = null;
let resizeRAF = null;
let lastWidth = 0;
let lastHeight = 0;

onMounted(async () => {
  await nextTick();
  const container = mapContainer.value;
  if (!container) return;

  mapboxgl.accessToken = props.type === "satellite" ? vikingAccessToken : accessToken;

  map = new mapboxgl.Map({
    // Bind to the element itself. Passing the id makes mapbox-gl resolve it with
    // getElementById, so two BaseMaps on one page (MigrationMap also hardcodes
    // id="map") would both attach to whichever div comes first in the DOM.
    container,
    style: props.type === "satellite" ? vikingMapStyle : mapStyle,
    center: [0, 20],
    zoom: 1,
    fadeDuration: 0,
    maxZoom: 10,
    minZoom: 0,
    pixelRatio: window.devicePixelRatio || 1,
  });

  map.on("load", () => {
    map.resize();
    emit("mapLoaded", map);
  });

  // ---- ResizeObserver with dimension change detection ----
  if (window.ResizeObserver) {
    resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      // Only resize if dimensions actually changed
      if (width === lastWidth && height === lastHeight) return;
      lastWidth = width;
      lastHeight = height;
      if (resizeRAF) cancelAnimationFrame(resizeRAF);
      resizeRAF = requestAnimationFrame(() => {
        if (map) map.resize();
        resizeRAF = null;
      });
    });
    resizeObserver.observe(container);
  }
});

onUnmounted(() => {
  if (resizeRAF) cancelAnimationFrame(resizeRAF);
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  if (map) {
    map.remove();
    map = null;
  }
});
</script>